import { 
  createSlice, 
  createAsyncThunk, 
  createSelector} from '@reduxjs/toolkit';
import { RootState } from '../index';
import { 
  WeightState, 
  WeightEntry, 
  WeightGoal 
} from '../../types/weight';

// Define initial state
const initialState: WeightState = {
  // User's weight entries
  entries: {},
  entryIds: [],
  // User's weight goal
  goal: null,
  // UI state
  isLoading: false,
  error: null,
  lastUpdated: null
};

// Async thunks
export const fetchWeightEntries = createAsyncThunk(
  'weight/fetchWeightEntries',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Use dynamic import to avoid circular dependencies
      const { getWeightEntries } = await import('../../services/firebase/weight.service');
      
      const entries = await getWeightEntries(userId);
      
      // Convert array to record object
      const entriesRecord: Record<string, WeightEntry> = {};
      entries.forEach(entry => {
        entriesRecord[entry.id] = entry;
      });

      return {
        entries: entriesRecord,
        entryIds: Object.keys(entriesRecord).sort((a, b) => {
          // Sort by date (newest first)
          return entriesRecord[b].date - entriesRecord[a].date;
        }),
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchWeightGoal = createAsyncThunk(
  'weight/fetchWeightGoal',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Use dynamic import to avoid circular dependencies
      const { getWeightGoal } = await import('../../services/firebase/weight.service');
      
      const goal = await getWeightGoal(userId);
      
      return {
        goal,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const addWeightEntry = createAsyncThunk(
  'weight/addWeightEntry',
  async (
    { 
      userId, 
      weight, 
      date, 
      unit, 
      notes 
    }: { 
      userId: string; 
      weight: number; 
      date: number; 
      unit: 'kg' | 'lb'; 
      notes?: string 
    }, 
    { rejectWithValue }
  ) => {
    try {
      // Use dynamic import to avoid circular dependencies
      const { addWeightEntry } = await import('../../services/firebase/weight.service');
      
      const newEntry: Omit<WeightEntry, 'id'> = {
        userId,
        weight,
        weightValue: weight, // For compatibility
        date,
        unit,
        notes,
        createdAt: Date.now(),
        dateCreated: Date.now(), // For compatibility
        updatedAt: Date.now()
      };
      
      const entryId = await addWeightEntry(newEntry);
      
      return {
        entry: {
          ...newEntry,
          id: entryId
        },
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const updateWeightEntry = createAsyncThunk(
  'weight/updateWeightEntry',
  async (
    {
      entryId,
      data
    }: {
      entryId: string;
      data: Partial<Omit<WeightEntry, 'id' | 'userId' | 'createdAt'>>
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const existingEntry = state.weight.entries[entryId];

      if (!existingEntry) {
        throw new Error(`Entry with ID ${entryId} not found`);
      }

      // Get userId from entry or fall back to auth state
      const userId = existingEntry.userId || state.auth?.user?.uid;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Use dynamic import to avoid circular dependencies
      const { updateWeightEntry } = await import('../../services/firebase/weight.service');

      const updatedEntry = {
        ...existingEntry,
        ...data,
        userId, // Ensure userId is set
        updatedAt: Date.now()
      };

      await updateWeightEntry(entryId, updatedEntry);

      return {
        entry: updatedEntry,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const deleteWeightEntry = createAsyncThunk(
  'weight/deleteWeightEntry',
  async (entryId: string, { rejectWithValue, getState }) => {
    try {
      // Get the state to extract userId from the entry or auth
      const state = getState() as RootState;
      const entry = state.weight.entries[entryId];

      // Get userId from entry or fall back to auth state
      const userId = entry?.userId || state.auth?.user?.uid;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      if (!entry) {
        throw new Error(`Entry with ID ${entryId} not found`);
      }

      // Use dynamic import to avoid circular dependencies
      const { deleteWeightEntry } = await import('../../services/firebase/weight.service');

      // Pass the userId
      await deleteWeightEntry(userId, entryId);

      return {
        entryId,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const updateWeightGoal = createAsyncThunk(
  'weight/updateWeightGoal',
  async (
    { 
      userId, 
      goal 
    }: { 
      userId: string; 
      goal: Partial<WeightGoal> 
    }, 
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const existingGoal = state.weight.goal;
      
      // Use dynamic import to avoid circular dependencies
      const { updateWeightGoal } = await import('../../services/firebase/weight.service');
      
      const updatedGoal = {
        ...existingGoal,
        ...goal,
        updatedAt: Date.now()
      } as WeightGoal;
      
      // If there was no existing goal, set createdAt
      if (!existingGoal) {
        updatedGoal.createdAt = Date.now();
      }
      
      await updateWeightGoal(userId, updatedGoal);
      
      return {
        goal: updatedGoal,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Create the weight slice
const weightSlice = createSlice({
  name: 'weight',
  initialState,
  reducers: {
    resetWeightError: (state) => {
      state.error = null;
    },
    clearWeightData: (state) => {
      state.entries = {};
      state.entryIds = [];
      state.goal = null;
      state.lastUpdated = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchWeightEntries
      .addCase(fetchWeightEntries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWeightEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entries = action.payload.entries;
        state.entryIds = action.payload.entryIds;
        state.lastUpdated = action.payload.lastUpdated;
      })
      .addCase(fetchWeightEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // fetchWeightGoal
      .addCase(fetchWeightGoal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWeightGoal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.goal = action.payload.goal;
        state.lastUpdated = action.payload.lastUpdated;
      })
      .addCase(fetchWeightGoal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // addWeightEntry
      .addCase(addWeightEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addWeightEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        const newEntry = action.payload.entry;
        
        // Add entry to state
        state.entries[newEntry.id] = newEntry;
        
        // Update entryIds (sorted by date, newest first)
        state.entryIds = [newEntry.id, ...state.entryIds].sort((a, b) => {
          return state.entries[b].date - state.entries[a].date;
        });
        
        // Update lastUpdated
        state.lastUpdated = action.payload.lastUpdated;
        
        // If this is the latest entry, update currentWeight in goal if goal exists
        if (state.goal && state.goal.isEnabled) {
          // Find the latest entry
          const latestEntryId = state.entryIds[0];
          const latestEntry = state.entries[latestEntryId];
          
          // Update currentWeight in goal
          state.goal.currentWeight = latestEntry.weight;
          state.goal.updatedAt = Date.now();
        }
      })
      .addCase(addWeightEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateWeightEntry
      .addCase(updateWeightEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWeightEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedEntry = action.payload.entry;
        
        // Update entry in state
        state.entries[updatedEntry.id] = updatedEntry;
        
        // Re-sort entryIds if the date changed
        state.entryIds = state.entryIds.sort((a, b) => {
          return state.entries[b].date - state.entries[a].date;
        });
        
        // Update lastUpdated
        state.lastUpdated = action.payload.lastUpdated;
        
        // If this is the latest entry, update currentWeight in goal if goal exists
        if (state.goal && state.goal.isEnabled) {
          // Find the latest entry
          const latestEntryId = state.entryIds[0];
          const latestEntry = state.entries[latestEntryId];
          
          // Update currentWeight in goal
          state.goal.currentWeight = latestEntry.weight;
          state.goal.updatedAt = Date.now();
        }
      })
      .addCase(updateWeightEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // deleteWeightEntry
      .addCase(deleteWeightEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWeightEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        const { entryId } = action.payload;
        
        // Remove entry from state
        delete state.entries[entryId];
        state.entryIds = state.entryIds.filter(id => id !== entryId);
        
        // Update lastUpdated
        state.lastUpdated = action.payload.lastUpdated;
        
        // If there are entries left and goal exists, update currentWeight
        if (state.entryIds.length > 0 && state.goal && state.goal.isEnabled) {
          // Find the latest entry
          const latestEntryId = state.entryIds[0];
          const latestEntry = state.entries[latestEntryId];
          
          // Update currentWeight in goal
          state.goal.currentWeight = latestEntry.weight;
          state.goal.updatedAt = Date.now();
        }
      })
      .addCase(deleteWeightEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateWeightGoal
      .addCase(updateWeightGoal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWeightGoal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.goal = action.payload.goal;
        state.lastUpdated = action.payload.lastUpdated;
      })
      .addCase(updateWeightGoal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

// Export actions
export const { 
  resetWeightError, 
  clearWeightData 
} = weightSlice.actions;

// Basic selectors
export const selectWeightState = (state: RootState) => state.weight;

export const selectWeightEntries = createSelector(
  selectWeightState,
  (state) => state.entries
);

export const selectWeightEntryIds = createSelector(
  selectWeightState,
  (state) => state.entryIds
);

export const selectWeightGoal = createSelector(
  selectWeightState,
  (state) => state.goal
);

export const selectWeightLoading = createSelector(
  selectWeightState,
  (state) => state.isLoading
);

export const selectWeightError = createSelector(
  selectWeightState,
  (state) => state.error
);

export const selectWeightLastUpdated = createSelector(
  selectWeightState,
  (state) => state.lastUpdated
);

// Complex selectors
export const selectWeightEntriesArray = createSelector(
  selectWeightEntries,
  selectWeightEntryIds,
  (entries, entryIds) => entryIds.map(id => entries[id])
);

export const selectLatestWeight = createSelector(
  selectWeightEntriesArray,
  (entries) => entries.length > 0 ? entries[0] : null
);

export const selectWeightEntryById = (id: string) => createSelector(
  selectWeightEntries,
  (entries) => entries[id] || null
);

export const selectWeightStats = createSelector(
  selectWeightEntriesArray,
  selectWeightGoal,
  (entries, goal) => {
    if (entries.length === 0) {
      return {
        currentWeight: 0,
        initialWeight: 0,
        lowestWeight: 0,
        highestWeight: 0,
        netChange: 0,
        goalProgress: 0,
        daysUntilDeadline: 0,
        hasGoal: false
      };
    }
    
    // Current weight is the latest entry
    const currentWeight = entries[0].weight || entries[0].weightValue || 0;
    
    // Initial weight is the first entry (oldest)
    const initialWeight = entries[entries.length - 1].weight || entries[entries.length - 1].weightValue || 0;
    
    // Lowest and highest weights
    const lowestWeight = Math.min(...entries.map(entry => entry.weight || entry.weightValue || 0));
    const highestWeight = Math.max(...entries.map(entry => entry.weight || entry.weightValue || 0));
    
    // Net change from initial to current
    const netChange = currentWeight - initialWeight;
    
    // Goal-related stats
    let goalProgress = 0;
    let daysUntilDeadline = 0;
    let hasGoal = false;
    
    if (goal && goal.isEnabled && goal.targetWeight) {
      hasGoal = true;
      
      // Calculate goal progress
      const initialToTarget = goal.targetWeight - initialWeight;
      const currentToInitial = currentWeight - initialWeight;
      
      // Avoid division by zero
      if (initialToTarget !== 0) {
        goalProgress = Math.min(100, Math.max(0, (1 - currentToInitial / initialToTarget) * 100));
      }
      
      // Calculate days until deadline
      const now = new Date().setHours(0, 0, 0, 0);
      daysUntilDeadline = Math.ceil((goal.deadline - now) / (1000 * 60 * 60 * 24));
    }
    
    return {
      currentWeight,
      initialWeight,
      lowestWeight,
      highestWeight,
      netChange,
      goalProgress,
      daysUntilDeadline,
      hasGoal
    };
  }
);

// Export reducer
export default weightSlice.reducer;